import { useSearch } from '@tanstack/react-router';
import { useDebounce } from '@uidotdev/usehooks';
import { useBooleanSetting } from 'hooks/settings/settings';
import { useState } from 'react';
import { SavedViewModelType, SettingName, UserKind } from 'src/api/generated';
import { SavedViewDropdownWrapper } from 'src/modules/communications/components/molecules/saved-view/saved-view-dropdown-wrapper';
import { UNASSIGNED_USER_ID } from 'src/modules/communications/constants/filters.const';
import { SavedViewProvider } from 'src/modules/communications/context/saved-view.context';
import { SpeedDialerProvider } from 'src/modules/communications/context/speed-dialer.context';
import { PropertyPreviewProvider } from 'src/modules/communications/contexts/property-preview-provider';
import { useConversationFilters } from 'src/modules/communications/hooks/advanced-filters/conversations/filters';
import { useConversationSorts } from 'src/modules/communications/hooks/advanced-filters/conversations/sorts';
import { useResolvedCommunicationsView } from 'src/modules/communications/hooks/use-resolved-communications-view';
import type { ConversationPanelProps } from 'src/modules/communications/types/conversation-panel.type';
import { COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY } from 'src/modules/communications/utils/view-preference.util';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { AdvancedFilters } from 'src/modules/shared/components/molecules/advanced-filters/filters/advanced-filters';
import { AdvancedSorts } from 'src/modules/shared/components/molecules/advanced-filters/sorts/advanced-sorts';
import { FunnelSelector } from 'src/modules/shared/components/molecules/funnel-selector';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import {
  UserSelector,
  type UserOption as UserSelectorOption,
} from 'src/modules/shared/components/molecules/user-selector';
import { useHasRoles } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { Route as CommunicationsRoute } from 'src/routes/communications/index';
import { InteractionsActionsDropdown } from '../../molecules/interactions/actions-dropdown';
import ViewSwitcher from '../../molecules/view-switcher';
import ConversationListView from './lists/conversation-list-view';
import { ConversationPipelineView } from './pipelines/conversation-pipeline-view';
import { ConversationTableView } from './tables/conversation-table-view';

const ConversationPanel: React.FC<ConversationPanelProps> = ({
  funnels,
  selectedFunnelId,
  onFunnelSelect,
  onConversationSelect,
  onOpenContactProfile,
  selectedConversationId,
  layout,
  children,
  aiStatusIds = [],
}) => {
  const { t } = useI18n();
  const { view } = useSearch({ from: CommunicationsRoute.id });
  const { resolvedView } = useResolvedCommunicationsView(view);
  const isSmallScreen = useMediaQuery(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY);
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 800);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const { setting: showAllConversations } = useBooleanSetting(
    SettingName.FEATURE_FLAG_AGENT_SHOW_ALL_CONVERSATIONS
  );

  const { hasRole: isAdmin } = useHasRoles('Admin');
  const showAgentSelector = showAllConversations || isAdmin;
  const isMobileConversationDetail =
    isSmallScreen && selectedConversationId !== undefined;

  const {
    fieldsOptions: filterFieldsOptions,
    activeFilterCount,
    filters,
    setFilters,
    userOptions,
    loading,
    apiFilters,
    statusOptions,
    propertyOptions,
    propertyLoading,
    onPropertyQueryChange,
    selectedUserIds,
    setSelectedUserIds,
    resetFilters,
  } = useConversationFilters({ selectedFunnelId });

  const {
    sorts,
    setSorts,
    activeSortCount,
    apiSorts,
    fieldsOptions: sortFieldsOptions,
    resetSorts,
  } = useConversationSorts();

  const handleUserSelectorChange = (userId: string | number) => {
    if (userId === '') {
      setSelectedUserIds([]);
      return;
    }

    if (userId === String(UNASSIGNED_USER_ID)) {
      setSelectedUserIds([UNASSIGNED_USER_ID]);
      return;
    }

    setSelectedUserIds([Number(userId)]);
  };

  const userSelectorOptions = (() => {
    const personOptions: UserSelectorOption[] = userOptions
      .filter((opt) => opt.kind === UserKind.PERSON)
      .map((opt) => ({
        id: String(opt.value),
        label: opt.label,
        kind: opt.kind,
      }));

    const systemOptions: UserSelectorOption[] = userOptions
      .filter((opt) => opt.kind === UserKind.SYSTEM)
      .map((opt) => ({
        id: String(opt.value),
        label: opt.label,
        kind: opt.kind,
      }));

    return [
      {
        id: String(UNASSIGNED_USER_ID),
        label: t('communications.panel.unassigned'),
      },
      ...personOptions,
      ...systemOptions,
    ];
  })();

  if (!selectedFunnelId) {
    return (
      <ErrorState
        title={t('communications.panel.noFunnel.title')}
        description={t('communications.panel.noFunnel.description')}
      />
    );
  }

  const desktopToolbar = (
    <PanelHeader
      border={false}
      fixed
      size="lg"
      scrollable
      className="px-3"
      containerClassName="items-center gap-2"
      childrenClassName="min-w-max"
      actions={
        <div className="flex items-center gap-1">
          <SavedViewDropdownWrapper
            filters={filters}
            setFilters={setFilters}
            sorts={sorts}
            setSorts={setSorts}
            fieldsOptions={filterFieldsOptions}
            resetFilters={resetFilters}
            resetSorts={resetSorts}
            compactActions
            className="flex-shrink-0"
            dropdownClassName="w-auto max-w-[12rem]"
            handlerContainerClassName="max-w-[12rem]"
          />

          <AdvancedFilters
            fieldsOptions={filterFieldsOptions}
            onChange={setFilters}
            activeFilterCount={activeFilterCount}
            filters={filters}
            userOptions={userOptions.map((user) => ({
              id: user.value,
              name: user.label,
            }))}
            statusOptions={statusOptions}
            propertyOptions={propertyOptions}
            propertyLoading={propertyLoading}
            onPropertyQueryChange={onPropertyQueryChange}
          />

          <AdvancedSorts
            fieldsOptions={sortFieldsOptions}
            onChange={setSorts}
            activeSortCount={activeSortCount}
            sorts={sorts}
          />
        </div>
      }
    >
      <div className="flex min-w-max items-center gap-2">
        <div className="flex-shrink-0">
          <InteractionsActionsDropdown
            funnelId={selectedFunnelId}
            onConversationCreated={onConversationSelect}
          />
        </div>

        <div className="flex-shrink-0">
          <ViewSwitcher />
        </div>

        <div className="min-w-[9rem] max-w-[14rem] flex-grow">
          <FunnelSelector
            funnels={funnels}
            selectedFunnelId={selectedFunnelId}
            onFunnelSelect={onFunnelSelect}
            size="md"
            className="w-auto max-w-[14rem]"
            showSettings={true}
            handlerContainerClassName="max-w-[14rem]"
            tooltipContent={(selectedFunnel) =>
              t('communications.panel.funnelTooltip', {
                funnelName: selectedFunnel.name,
              })
            }
          />
        </div>

        {showAgentSelector && (
          <div className="min-w-[9rem] max-w-[13rem] flex-grow">
            <UserSelector
              users={userSelectorOptions}
              selectedUserIds={selectedUserIds}
              multipleSelectionLabel={(count) =>
                t('communications.panel.assigneesCount', {
                  count: String(count),
                })
              }
              onUserSelect={handleUserSelectorChange}
              size="md"
              className="w-auto max-w-[13rem]"
              handlerContainerClassName="max-w-[13rem]"
              loading={loading}
              allLabel={t('communications.panel.allAssignees')}
              tooltipContent={(selectedLabel) =>
                t('communications.panel.assigneeTooltip', {
                  assignee: selectedLabel,
                })
              }
            />
          </div>
        )}

        <div className="min-w-[12rem] max-w-[17rem] flex-grow">
          <LabelTextInput
            label={t('communications.panel.searchLabel')}
            labelPosition="top"
            size="md"
            inputProps={{
              className: 'w-full',
              placeholder: t('communications.panel.searchPlaceholder'),
              value: searchTerm,
              onChange: (e) => handleSearch(e.target.value),
            }}
          />
        </div>
      </div>
    </PanelHeader>
  );

  const mobileListToolbar = (
    <PanelHeader
      border={false}
      className="px-3 py-3"
      containerClassName="flex-col items-stretch gap-3 px-0 py-0"
      childrenClassName="w-full"
      collapsible={false}
    >
      <div className="flex w-full flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <InteractionsActionsDropdown
                funnelId={selectedFunnelId}
                onConversationCreated={onConversationSelect}
              />
            </div>

            <div className="flex-shrink-0">
              <ViewSwitcher />
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center gap-1">
            <AdvancedFilters
              fieldsOptions={filterFieldsOptions}
              onChange={setFilters}
              activeFilterCount={activeFilterCount}
              filters={filters}
              userOptions={userOptions.map((user) => ({
                id: user.value,
                name: user.label,
              }))}
              statusOptions={statusOptions}
              propertyOptions={propertyOptions}
              propertyLoading={propertyLoading}
              onPropertyQueryChange={onPropertyQueryChange}
            />

            <AdvancedSorts
              fieldsOptions={sortFieldsOptions}
              onChange={setSorts}
              activeSortCount={activeSortCount}
              sorts={sorts}
            />
          </div>
        </div>

        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <TextInput
              placeholder={t('communications.panel.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        <div className="-mx-3 overflow-x-auto px-3 pb-1">
          <div className="flex min-w-max items-center gap-2">
            <SavedViewDropdownWrapper
              filters={filters}
              setFilters={setFilters}
              sorts={sorts}
              setSorts={setSorts}
              fieldsOptions={filterFieldsOptions}
              resetFilters={resetFilters}
              resetSorts={resetSorts}
              size="sm"
              compactActions
              className="flex-shrink-0"
              dropdownClassName="w-auto max-w-[9.75rem] rounded-full"
              handlerContainerClassName="max-w-[9.75rem]"
            />

            <FunnelSelector
              funnels={funnels}
              selectedFunnelId={selectedFunnelId}
              onFunnelSelect={onFunnelSelect}
              size="sm"
              className="w-auto max-w-[11rem] rounded-full"
              showSettings={true}
              handlerContainerClassName="max-w-[11rem]"
              tooltipContent={(selectedFunnel) =>
                t('communications.panel.funnelTooltip', {
                  funnelName: selectedFunnel.name,
                })
              }
            />

            {showAgentSelector && (
              <UserSelector
                users={userSelectorOptions}
                selectedUserIds={selectedUserIds}
                multipleSelectionLabel={(count) =>
                  t('communications.panel.assigneesCount', {
                    count: String(count),
                  })
                }
                onUserSelect={handleUserSelectorChange}
                size="sm"
                className="w-auto max-w-[9rem] rounded-full"
                handlerContainerClassName="max-w-[9rem]"
                loading={loading}
                allLabel={t('communications.panel.allAssignees')}
                tooltipContent={(selectedLabel) =>
                  t('communications.panel.assigneeTooltip', {
                    assignee: selectedLabel,
                  })
                }
              />
            )}
          </div>
        </div>
      </div>
    </PanelHeader>
  );

  return (
    <PropertyPreviewProvider>
      <SavedViewProvider model={SavedViewModelType.CONVERSATION}>
        <div className="flex flex-col h-full">
          {!isMobileConversationDetail && (
            <div className="w-full relative z-10 border-b border-gray-200 bg-white">
              {isSmallScreen ? mobileListToolbar : desktopToolbar}
            </div>
          )}

          <div className="flex flex-1 overflow-hidden">
            <div
              className={`${layout?.conversationListWidth ?? 'w-full'} transition-all duration-200`}
            >
              {resolvedView === 'cards' && (
                <Panel
                  variant="neutral"
                  appearance="ghost"
                  className="flex-1 h-full p-2.5"
                >
                  <div className="flex p-2.5 bg-white rounded-lg h-full">
                    <ConversationListView
                      selectedFunnelId={selectedFunnelId}
                      searchTerm={debouncedSearch}
                      onConversationSelect={onConversationSelect}
                      onOpenContactProfile={onOpenContactProfile}
                      selectedConversationId={selectedConversationId}
                      filters={apiFilters}
                      sorts={apiSorts}
                    />
                  </div>
                </Panel>
              )}
              {resolvedView === 'pipeline' && (
                <Panel
                  shadow="flat"
                  variant="neutral"
                  className="flex-1 h-full rounded-none px-2 pt-3 bg-white"
                >
                  <ConversationPipelineView
                    selectedFunnelId={selectedFunnelId}
                    searchTerm={debouncedSearch}
                    onConversationSelect={onConversationSelect}
                    onOpenContactProfile={onOpenContactProfile}
                    selectedConversationId={selectedConversationId}
                    filters={apiFilters}
                    sorts={apiSorts}
                    aiStatusIds={aiStatusIds}
                  />
                </Panel>
              )}
              {resolvedView === 'table' && (
                <Panel
                  shadow="flat"
                  variant="neutral"
                  className="flex-1 h-full overflow-y-auto bg-white rounded-none"
                >
                  <SpeedDialerProvider>
                    <ConversationTableView
                      selectedFunnelId={selectedFunnelId}
                      searchTerm={debouncedSearch}
                      onConversationSelect={onConversationSelect}
                      selectedConversationId={selectedConversationId}
                      filters={apiFilters}
                      sorts={apiSorts}
                    />
                  </SpeedDialerProvider>
                </Panel>
              )}
            </div>

            {children}
          </div>
        </div>
      </SavedViewProvider>
    </PropertyPreviewProvider>
  );
};

export default ConversationPanel;
